import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import { toast } from "sonner";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Initialize Supabase client
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Iter40 — global FLAC 403 interceptor (one-time setup)
let _flacInterceptorInstalled = false;
function _installFlacInterceptor() {
  if (_flacInterceptorInstalled) return;
  _flacInterceptorInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail && typeof detail === "object" && detail.code === "flac_write_denied") {
        const fields = (detail.violations || []).map((v) => v.field_key).join(", ");
        toast.error(
          `Acces interzis: nu poți modifica ${fields}.`,
          {
            id: "flac-write-denied",
            duration: 6000,
            description: detail.message || "Contactează administratorul pentru drepturi suplimentare.",
          }
        );
      }
      return Promise.reject(error);
    }
  );
}

// Permission matrix (same as backend)
const PERMISSIONS = {
  "admin": {"sales": ["create", "read", "update", "delete", "export"], "clients": ["create", "read", "update", "delete"], "users": ["create", "read", "update", "delete"], "agencies": ["create", "read", "update", "delete"], "products": ["create", "read", "update", "delete"], "edit_requests": ["approve"]},
  "backoffice": {"sales": ["create", "read", "update", "delete"], "clients": ["create", "read", "update", "delete"], "users": ["read"], "agencies": ["read"], "products": ["create", "read", "update", "delete"], "edit_requests": ["approve"]},
  "manager": {"sales": ["create", "read", "update"], "clients": ["create", "read", "update"], "users": ["read"], "agencies": ["read"], "products": ["read"]},
  "agentie": {"sales": ["create", "read"], "clients": ["create", "read"], "users": ["read"], "agencies": ["read"], "products": ["read"]},
  "agent": {"sales": ["create", "read"], "clients": ["create", "read"], "users": [], "agencies": ["read"], "products": ["read"]}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  // Iter41 — global FLAC scope (one fetch per session, shared across all components)
  const [fieldAccess, setFieldAccess] = useState(null);

  // Helper: fetch FLAC scope for current user
  const _fetchFieldAccess = async () => {
    try {
      const r = await axios.get(`${API_URL}/me/field-access`);
      setFieldAccess(r.data);
    } catch {
      setFieldAccess({ role: null, enforced: false, hidden_fields: [], read_only_fields: [] });
    }
  };

  useEffect(() => {
    // Iter40 — install global FLAC error interceptor once
    _installFlacInterceptor();
    // Check for existing session
    checkSession();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setToken(session.access_token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${session.access_token}`;
        await fetchUser(session.access_token);
        await _fetchFieldAccess();
      } else {
        setToken(null);
        setUser(null);
        setFieldAccess(null);
        delete axios.defaults.headers.common["Authorization"];
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setToken(session.access_token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${session.access_token}`;
        await fetchUser(session.access_token);
        await _fetchFieldAccess();
      }
    } catch (error) {
      console.error("Session check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      // Don't logout on error - token might still be valid
    }
  };

  const login = async (email, password) => {
    // Login through backend (which handles Supabase Auth internally)
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Store Supabase session in the client
    if (access_token && refresh_token) {
      await supabase.auth.setSession({
        access_token,
        refresh_token
      });
    }
    
    setToken(access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setUser(userData);
    // Iter41 — fetch FLAC scope post-login
    await _fetchFieldAccess();
    
    return userData;
  };

  const register = async (email, password, name, role = "agent", agency_id = null) => {
    // Register with backend (which handles Supabase Auth)
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      name, 
      role, 
      agency_id 
    });
    
    // Auto login after registration
    return await login(email, password);
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setFieldAccess(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === "string") return user.role === roles;
    return roles.includes(user.role);
  };

  const hasPermission = (resource, action) => {
    if (!user) return false;
    const rolePerms = PERMISSIONS[user.role] || {};
    const resourcePerms = rolePerms[resource] || [];
    return resourcePerms.includes(action);
  };

  const canExport = () => hasPermission("sales", "export");
  const canEdit = () => hasPermission("sales", "update");
  const canDelete = () => hasPermission("sales", "delete");
  const canManageUsers = () => hasPermission("users", "create");
  const canManageAgencies = () => hasPermission("agencies", "create");
  const canManageProducts = () => hasPermission("products", "create");
  const canApproveEdits = () => hasPermission("edit_requests", "approve");

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      hasRole, 
      hasPermission,
      canExport,
      canEdit,
      canDelete,
      canManageUsers,
      canManageAgencies,
      canManageProducts,
      canApproveEdits,
      token,
      fieldAccess,  // Iter41 — global FLAC scope (null while loading)
      supabase  // Export supabase client for storage etc.
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
